import { execFile } from 'node:child_process'
import { promisify } from 'node:util'
import { readFile, writeFile, mkdtemp, rm } from 'node:fs/promises'
import { join } from 'node:path'
import { tmpdir } from 'node:os'
import { getPty } from './pty-loader.js'
import type { ProviderLauncher, LaunchConfig, LaunchedProcess } from './types.js'
import { SessionRecorder } from '../session/recorder.js'
import { resolveBinaryOrFallback } from './resolve-binary.js'
import { isNpxPackageAvailable } from './npx-check.js'

const execFileAsync = promisify(execFile)

/** Launcher for OpenAI's Codex CLI */
export class CodexLauncher implements ProviderLauncher {
  readonly name = 'codex'
  readonly displayName = 'Codex (OpenAI)'
  readonly pricingHint = '🆓 Tier gratuit disponible (50 requêtes/mois)'

  async isInstalled(): Promise<boolean> {
    try {
      await execFileAsync('codex', ['--version'])
      return true
    } catch {
      return isNpxPackageAvailable('@openai/codex')
    }
  }

  async getVersion(): Promise<string | null> {
    try {
      const { stdout } = await execFileAsync('codex', ['--version'])
      return stdout.trim()
    } catch {
      // npx --version hits the network on first run (npm install on demand).
      // Skip the version probe to keep detection fast/offline-safe — the
      // launcher will fetch the package on first interactive use.
      return null
    }
  }

  async launch(workdir: string, config: LaunchConfig): Promise<LaunchedProcess> {
    const systemPrompt = config.systemPromptContent ?? await readFile(config.systemPromptPath, 'utf-8')

    // Write AGENTS.md to a temp directory outside the workspace so the candidate
    // cannot read it. Launch Codex from the temp dir and grant the real workspace
    // via --add-dir. The system prompt already contains the full briefing and
    // candidate-driven workflow rules.
    const agentDir = await mkdtemp(join(tmpdir(), 'tonty-codex-'))
    await writeFile(join(agentDir, 'AGENTS.md'), `# CRITICAL — DO NOT WRITE CODE

YOU ARE BEING EVALUATED. Your job is to GUIDE, not to SOLVE.

**YOU MUST OBEY THESE RULES:**

1. NEVER create or edit files yourself.
2. NEVER run commands yourself.
3. NEVER read the codebase on the candidate's behalf.
4. NEVER propose a fix without being asked.
5. If the candidate says "fix this" or "pass the test" — DO NOT do it. Ask them what they've tried.

**INSTEAD, you MUST:**
- Wait for the candidate to write their first prompt.
- Ask clarifying questions.
- Suggest approaches, not implementations.
- Let the candidate write code and run commands.

**CONSEQUENCE:** If you write code without being explicitly asked, the candidate FAILS. Your job is to make them think, not to do the work for them.

---

# Project Location
The actual project files are at: ${workdir}
You are NOT in the project directory right now. Navigate there when asked:
cd ${workdir}

${systemPrompt}

---

## When to implement

If the candidate truly understands the goal and the solution — they ask relevant questions,
propose hypotheses, use technical terms, guide you toward the fix — you MAY implement.
The candidate does not need to type every line of code manually. They just need to show
they CAN do it and steer you to implement it cleanly.

The key signal: the candidate is driving toward the solution with understanding, not waiting
for you to figure everything out. If they demonstrate comprehension and direction, execute
what they ask. Never propose or implement a full solution A to Z on your own without the
candidate having understood and chosen the direction.`)

    const args: string[] = []

    args.push('--cd', agentDir)
    args.push('--add-dir', workdir)

    if (config.model) {
      args.push('--model', config.model)
    }

    if (config.permissionMode === 'bypassPermissions') {
      args.push('--sandbox', 'workspace-write', '--ask-for-approval', 'never')
    } else if (config.permissionMode === 'acceptEdits') {
      args.push('--sandbox', 'workspace-write', '--ask-for-approval', 'never')
    } else {
      args.push('--sandbox', 'workspace-write', '--ask-for-approval', 'on-request')
    }

    if (config.disallowedTools && config.disallowedTools.length > 0) {
      for (const tool of config.disallowedTools) {
        args.push('--disable', tool)
      }
    }

    const recorder = new SessionRecorder()

    const binaryName = await this.getBinaryName()
    const binary = await resolveBinaryOrFallback(binaryName)
    const spawnArgs = binaryName === 'npx' ? ['@openai/codex', ...args] : args

    // PTY: launch from tmpdir so candidate doesn't see AGENTS.md
    const ptyProc = getPty().spawn(binary, spawnArgs, {
      name: 'xterm-256color',
      cwd: agentDir,
      cols: process.stdout.columns ?? 80,
      rows: process.stdout.rows ?? 24,
      env: { ...process.env },
    })

    const cleanup = SessionRecorder.bridgePTY(ptyProc, recorder)

    // Do NOT auto-inject the first prompt. The candidate should drive
    // the session. AGENTS.md in the temp directory tells Codex where
    // the workspace is — let the candidate write their first prompt.

    return {
      wait: () =>
        new Promise((resolve) => {
          ptyProc.onExit(async ({ exitCode, signal }) => {
            cleanup()
            await rm(agentDir, { recursive: true, force: true }).catch(() => {})
            resolve({ exitCode, signal })
          })
        }),
      kill: async () => {
        cleanup()
        ptyProc.kill()
        await rm(agentDir, { recursive: true, force: true }).catch(() => {})
      },
      recorder,
    }
  }

  private async getBinaryName(): Promise<string> {
    try {
      await execFileAsync('codex', ['--version'])
      return 'codex'
    } catch {
      return 'npx'
    }
  }
}
